/* eslint-disable import/no-unresolved */
import {
    useMutation, useQuery, useLazyQuery, useReactiveVar,
} from '@apollo/client';
import { Button } from 'appkit-react';
import { useState, useEffect } from 'react';

import {
    CREATE_API_KEY, DELETE_DOMAIN_URL, EDIT_USER, IAM_UPDATE_USER,
} from 'Apollo/Mutations';
import { GET_USER_INFO, GET_USERS_FOR_PROFILE } from 'Apollo/Queries';
import {
    leftNavigationItemsVar, tenantIdVar, showDataReconMenusVar, showCrossReportConfigVar,
} from 'Apollo/config';
import ProfilePage from '../components/ProfilePage';

// product name
const productName = process.env.REACT_APP_PRODUCT;

const ProfilePageContainer = () => {
    const [visible, setVisible] = useState(false);
    const [showApiKeyAlert, setShowApiKeyAlert] = useState(false);
    const [apiKeyResult, setApiKeyResult] = useState('');

    const [showEditUserFields, setShowEditUserFields] = useState(false);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [jobTitle, setJobTitle] = useState('');

    const [selectedUser, setSelectedUser] = useState();

    const [isApiKeyCopied, setIsApiKeyCopied] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [userUpdatedTenantList, setUserUpdatedTenantList] = useState([]);

    const currentActiveTenantId = useReactiveVar(tenantIdVar);

    /* apollo query to fetch the user information */
    const {
        loading, data = {}, refetch,
    } = useQuery(GET_USER_INFO, {
        fetchPolicy: 'no-cache',
    });

    const userData = data && data.getUser;
    const domains = userData && data.getUser.iamTenant && data.getUser.iamTenant.domainUrls && data.getUser.iamTenant.domainUrls.map(domain => ({ name: domain }));

    const tenantKey = data && data.getUser && data.getUser.iamTenant && data.getUser.iamTenant.tenantId;

    /* Apollo query to fetch row details  */
    const [getUsersProfile, users] = useLazyQuery(GET_USERS_FOR_PROFILE, {
        variables: {
            input: {
                tenantId: currentActiveTenantId,
                getAll: true,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Apollo mutation to edit user in TCR */
    const [editUser, { loading: editUserLoading }] = useMutation(EDIT_USER);

    /* Apollo mutation to edit user in IAM */
    const [updateUser, { loading: updateUserLoading }] = useMutation(IAM_UPDATE_USER, {
        variables: {
            input: {
                firstName,
                lastName,
                email: userData && userData.email,
                // ✅ FIX: Use optional chaining for iamOrganizationId
                organizationId: userData?.iamTenant?.iamOrganizationId || null,
                mobileNumber: phone !== 'NA' ? phone : null,
                product: productName,
            },
        },
    });

    /* apollo mutation to delete domain */
    const [deleteDomainUrl] = useMutation(DELETE_DOMAIN_URL);

    /* apollo mutation to generate API key */
    const [createAPIKey, { loading: createAPIKeyLoading }] = useMutation(CREATE_API_KEY, {
        variables: {
            input: {
                userId: parseInt(selectedUser, 10),
            },
        },
    });

    const getApiKey = async () => {
        setIsApiKeyCopied(false);
        setShowApiKey(false);
        let response;
        try {
            response = await createAPIKey();
            if (response) {
                setApiKeyResult(response.data.createAPIKey.apiKey);
                setShowApiKeyAlert(true);
            } else {
                setShowApiKeyAlert(false);
            }
        } catch (e) {
            //
        }
    };

    /**
     * ✅ Helper: Find the selected tenant from userTenants.
     * V2 response has tenantId directly on the tenant object.
     * Old code used UserTenantRole.tenantId which no longer exists.
     */
    const findSelectedTenant = (userTenants, activeTenantId) => {
        if (!userTenants || !activeTenantId) return null;
        return userTenants.find(ut => {
            // ✅ V2: tenantId is directly on the tenant object
            const tid = ut?.UserTenantRole?.tenantId || ut?.tenantId;
            return tid === activeTenantId;
        }) || null;
    };

    const onClickEditUserInfo = () => {
        const user = data?.getUser;
        if (!user) return;

        const isInValidFirstName = !user.firstName;
        const isInvalidLastName = !user.lastName;
        const isInvalidPhone = !user.phone || user.phone > 14;

        const selectedTenant = findSelectedTenant(user.userTenants, currentActiveTenantId);

        const currentJobTitle = selectedTenant?.UserTenantRole?.jobTitle || '';
        const isInvalidJobTitle = !currentJobTitle;

        setFirstName(isInValidFirstName ? 'NA' : user.firstName);
        setLastName(isInvalidLastName ? 'NA' : user.lastName);
        setPhone(isInvalidPhone ? 'NA' : user.phone);
        setJobTitle(isInvalidJobTitle ? 'NA' : currentJobTitle);
        setShowEditUserFields(true);
    };

    const onSaveUserInfo = async () => {
        try {
            await updateUser();
            await editUser({
                variables: {
                    input: {
                        firstName,
                        lastName,
                        phone: phone !== 'NA' ? phone : null,
                        userId: userData && userData.userId,
                        userTenants: userUpdatedTenantList,
                    },
                },
            });
            await refetch();
            setShowEditUserFields(false);
        } catch (e) {
            // catch block
        }
    };

    const unsecuredCopyToClipboard = text => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Unable to copy to clipboard', err);
        }
        document.body.removeChild(textArea);
    };

    const copyApiKey = key => {
        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(key);
        } else {
            unsecuredCopyToClipboard(key);
        }
        setIsApiKeyCopied(true);
    };

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
            minWidth: 500,
            resizable: true,
            sortable: true,
        },
        {
            Header: '',
            accessor: '',
            minWidth: 35,
            resizable: true,
            sortable: true,
            Cell: ({ original }) => {
                const onDelete = async () => {
                    try {
                        await deleteDomainUrl({
                            variables: {
                                input: {
                                    domainURL: original.name,
                                    tenantId: tenantKey,
                                },
                            },
                        });
                        refetch();
                    } catch (e) {
                        //
                    }
                };
                return (
                    <Button
                        kind="secondary"
                        style={{ marginTop: '0.3rem' }}
                        onClick={onDelete}
                    >
                        Delete
                    </Button>
                );
            },
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar([]);
        showDataReconMenusVar(false);
        showCrossReportConfigVar(false);
    }, []);

    useEffect(() => {
        if (tenantKey) {
            getUsersProfile();
        }
    }, [tenantKey]);

    /**
     * ✅ FIX: Build tenant list from userTenants.
     * V2 doesn't have UserTenantRole nested object — use tenantId directly.
     */
    useEffect(() => {
        if (data?.getUser?.userTenants) {
            const user = data.getUser;
            const selectedTenant = findSelectedTenant(user.userTenants, currentActiveTenantId);

            // ✅ Set jobTitle from UserTenantRole if it exists, otherwise empty
            setJobTitle(selectedTenant?.UserTenantRole?.jobTitle || '');

            const tenantList = user.userTenants.map(ut => ({
                department: ut?.UserTenantRole?.department || null,
                jobTitle: ut?.UserTenantRole?.jobTitle || null,
                // ✅ V2: tenantId is directly on tenant object
                tenantId: ut?.UserTenantRole?.tenantId || ut?.tenantId,
                userRoleId: ut?.UserTenantRole?.userRoleId || null,
                userTenantRoleId: ut?.UserTenantRole?.userTenantRoleId || null,
                userId: user.userId,
            }));
            setUserUpdatedTenantList(tenantList);
        }
    }, [data]);

    useEffect(() => {
        setUserUpdatedTenantList(prevState => {
            let prevData = [...prevState];
            prevData = prevData.map(t => (t.tenantId === currentActiveTenantId
                ? { ...t, jobTitle } : { ...t }));
            return prevData;
        });
    }, [jobTitle]);

    return (
        <ProfilePage
            userData={data && data.getUser}
            loading={loading}
            columns={columns}
            visible={visible}
            setVisible={setVisible}
            getApiKey={getApiKey}
            showApiKeyAlert={showApiKeyAlert}
            setShowApiKeyAlert={setShowApiKeyAlert}
            createAPIKeyLoading={createAPIKeyLoading}
            apiKeyResult={apiKeyResult}
            domains={domains}
            refetchUserInfo={refetch}
            showEditUserFields={showEditUserFields}
            setShowEditUserFields={setShowEditUserFields}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            phone={phone}
            setPhone={setPhone}
            jobTitle={jobTitle}
            setJobTitle={setJobTitle}
            onClickEditUserInfo={onClickEditUserInfo}
            onSaveUserInfo={onSaveUserInfo}
            editUserLoading={editUserLoading}
            updateUserLoading={updateUserLoading}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            copyApiKey={copyApiKey}
            isApiKeyCopied={isApiKeyCopied}
            showApiKey={showApiKey}
            setShowApiKey={setShowApiKey}
            users={users}
            currentActiveTenantId={currentActiveTenantId}
        />
    );
};

export default ProfilePageContainer;